import { Card, Text } from "@overmap-ai/blocks"
import { type Procedure, type Stored } from "@overmap-ai/common"
import { selectProcedureTypeById, selectUserById } from "@overmap-ai/core"
import { Link } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { AssetTimelineItemType } from "../../enums/assets"
import { assetTimelineItemTypeData } from "../../mappings/assets"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import { ProcedureIndexLabel } from "../IndexLabels/ProcedureIndexLabel"
import { TimelineIconRail } from "../TimelineIconRail/TimelineIconRail"
import type { AssetTimelineSize } from "./typings"

interface AssetProcedureTimelineItemProps {
	procedure: Stored<Procedure>
	size: AssetTimelineSize
}

export const AssetProcedureTimelineItem = memo<AssetProcedureTimelineItemProps>((props) => {
	const { procedure, size } = props

	const procedureType = useAppSelector(selectProcedureTypeById(procedure.procedure_type))!
	const createdBy = useAppSelector(selectUserById(procedure.created_by ?? -1))

	const { icon } = assetTimelineItemTypeData[AssetTimelineItemType.PROCEDURE]

	return (
		<div className="flex h-max w-full gap-4">
			<TimelineIconRail icon={icon} size={size} />
			<Card className="flex min-w-0 grow flex-col gap-1" size={size}>
				<Text size={size} accentColor="base">
					<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
					&nbsp;started a procedure
					<Text accentColor="base">&nbsp;&#8226;&nbsp;{formatCompactDate(procedure.submitted_at)}</Text>
				</Text>

				<Text accentColor="base" className="min-w-0 truncate" size={size}>
					<ProcedureTypeIcon className="align-sub" procedureTypeId={procedure.procedure_type} />
					&nbsp;
					<Link
						from="/$organizationId/$projectId"
						to="/$organizationId/$projectId/procedures/$procedureId"
						className={cx(interactiveTextCva())}
						params={{
							procedureId: procedure.uuid,
						}}
					>
						<ProcedureIndexLabel procedureId={procedure.uuid} highContrast />
						&nbsp;
						<Text accentColor="base">{procedureType.name}</Text>
					</Link>
				</Text>
			</Card>
		</div>
	)
})
AssetProcedureTimelineItem.displayName = "AssetProcedureTimelineItem"
